/**
 * Annotation 유틸리티 함수
 * 좌표 변환 및 Canvas 렌더링 관련 함수
 */

import {
  Annotation,
  AnnotationPoint,
  HighlighterAnnotation,
  UnderlineAnnotation,
} from '../types/Annotation';

/**
 * 화면 좌표를 상대 좌표(0~1)로 변환
 * @param screenX 화면 X 좌표 (픽셀)
 * @param screenY 화면 Y 좌표 (픽셀)
 * @param imageWidth 이미지 너비 (픽셀)
 * @param imageHeight 이미지 높이 (픽셀)
 * @returns 상대 좌표 (0.0 ~ 1.0)
 */
export function toRelativeCoord(
  screenX: number,
  screenY: number,
  imageWidth: number,
  imageHeight: number
): AnnotationPoint {
  // 0으로 나누는 것 방지
  if (imageWidth === 0 || imageHeight === 0) {
    return { x: 0, y: 0 };
  }

  return {
    x: Math.max(0, Math.min(1, screenX / imageWidth)),
    y: Math.max(0, Math.min(1, screenY / imageHeight)),
  };
}

/**
 * 상대 좌표(0~1)를 화면 좌표로 변환
 * @param point 상대 좌표 (0.0 ~ 1.0)
 * @param imageWidth 이미지 너비 (픽셀)
 * @param imageHeight 이미지 높이 (픽셀)
 * @returns 화면 좌표 (픽셀)
 */
export function toScreenCoord(
  point: AnnotationPoint,
  imageWidth: number,
  imageHeight: number
): { x: number; y: number } {
  return {
    x: point.x * imageWidth,
    y: point.y * imageHeight,
  };
}

/**
 * 형광펜 annotation을 Canvas에 렌더링
 * @param ctx Canvas 2D 컨텍스트
 * @param annotation 형광펜 annotation
 * @param canvasWidth Canvas 너비
 * @param canvasHeight Canvas 높이
 */
export function renderHighlighter(
  ctx: CanvasRenderingContext2D,
  annotation: HighlighterAnnotation,
  canvasWidth: number,
  canvasHeight: number
): void {
  if (annotation.points.length < 2) return;

  ctx.save();

  // 형광펜 스타일 설정
  ctx.globalAlpha = annotation.opacity;
  ctx.strokeStyle = annotation.color;
  ctx.lineWidth = annotation.strokeWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // 경로 그리기
  ctx.beginPath();
  const firstPoint = toScreenCoord(annotation.points[0], canvasWidth, canvasHeight);
  ctx.moveTo(firstPoint.x, firstPoint.y);

  for (let i = 1; i < annotation.points.length; i++) {
    const point = toScreenCoord(annotation.points[i], canvasWidth, canvasHeight);
    ctx.lineTo(point.x, point.y);
  }

  ctx.stroke();
  ctx.restore();
}

/**
 * 밑줄 annotation을 Canvas에 렌더링
 * @param ctx Canvas 2D 컨텍스트
 * @param annotation 밑줄 annotation
 * @param canvasWidth Canvas 너비
 * @param canvasHeight Canvas 높이
 */
export function renderUnderline(
  ctx: CanvasRenderingContext2D,
  annotation: UnderlineAnnotation,
  canvasWidth: number,
  canvasHeight: number
): void {
  ctx.save();

  const startScreen = toScreenCoord(annotation.startPoint, canvasWidth, canvasHeight);
  const endScreen = toScreenCoord(annotation.endPoint, canvasWidth, canvasHeight);

  ctx.strokeStyle = annotation.color;
  ctx.lineWidth = annotation.strokeWidth;
  ctx.lineCap = 'round';

  // 스타일에 따른 선 설정
  switch (annotation.style) {
    case 'dashed':
      ctx.setLineDash([10, 5]);
      break;
    case 'wavy':
      // 물결선은 별도 처리
      renderWavyLine(ctx, startScreen, endScreen, annotation.strokeWidth);
      ctx.restore();
      return;
    case 'solid':
    default:
      ctx.setLineDash([]);
      break;
  }

  // 직선 그리기
  ctx.beginPath();
  ctx.moveTo(startScreen.x, startScreen.y);
  ctx.lineTo(endScreen.x, endScreen.y);
  ctx.stroke();

  ctx.restore();
}

/**
 * 물결선 렌더링 (밑줄 스타일용)
 */
function renderWavyLine(
  ctx: CanvasRenderingContext2D,
  start: { x: number; y: number },
  end: { x: number; y: number },
  lineWidth: number
): void {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  if (distance === 0) return;

  const waveHeight = lineWidth * 1.5;
  const waveLength = lineWidth * 4;
  const numWaves = Math.ceil(distance / waveLength);

  ctx.beginPath();
  ctx.moveTo(start.x, start.y);

  // 방향 벡터 (단위 벡터)
  const ux = dx / distance;
  const uy = dy / distance;

  // 수직 벡터
  const px = -uy;
  const py = ux;

  for (let i = 0; i < numWaves; i++) {
    const t1 = (i + 0.25) / numWaves;
    const t2 = (i + 0.5) / numWaves;
    const t3 = (i + 0.75) / numWaves;
    const t4 = (i + 1) / numWaves;

    const x1 = start.x + dx * t1;
    const y1 = start.y + dy * t1 + waveHeight * (px * 0 + py * 0);
    const x2 = start.x + dx * t2 + px * waveHeight;
    const y2 = start.y + dy * t2 + py * waveHeight;
    const x3 = start.x + dx * t3;
    const y3 = start.y + dy * t3;
    const x4 = start.x + dx * t4 - px * waveHeight;
    const y4 = start.y + dy * t4 - py * waveHeight;

    ctx.quadraticCurveTo(x2, y2, start.x + dx * ((i + 0.5) / numWaves), start.y + dy * ((i + 0.5) / numWaves));
    ctx.quadraticCurveTo(x4, y4, start.x + dx * ((i + 1) / numWaves), start.y + dy * ((i + 1) / numWaves));
  }

  ctx.stroke();
}

/**
 * 모든 annotation을 Canvas에 렌더링
 * @param ctx Canvas 2D 컨텍스트
 * @param annotations annotation 배열
 * @param canvasWidth Canvas 너비
 * @param canvasHeight Canvas 높이
 */
export function renderAnnotations(
  ctx: CanvasRenderingContext2D,
  annotations: Annotation[],
  canvasWidth: number,
  canvasHeight: number
): void {
  // Canvas 초기화
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);

  // 생성 시간순으로 렌더링 (오래된 것이 아래)
  const sortedAnnotations = [...annotations].sort((a, b) => a.createdAt - b.createdAt);

  sortedAnnotations.forEach((annotation) => {
    if (annotation.type === 'highlighter') {
      renderHighlighter(ctx, annotation, canvasWidth, canvasHeight);
    } else if (annotation.type === 'underline') {
      renderUnderline(ctx, annotation, canvasWidth, canvasHeight);
    }
  });
}

/**
 * 점이 annotation 위에 있는지 확인 (지우개용)
 * @param point 화면 좌표
 * @param annotation annotation
 * @param canvasWidth Canvas 너비
 * @param canvasHeight Canvas 높이
 * @param threshold 임계값 (픽셀)
 * @returns annotation 위에 있으면 true
 */
export function isPointOnAnnotation(
  point: { x: number; y: number },
  annotation: Annotation,
  canvasWidth: number,
  canvasHeight: number,
  threshold: number = 10
): boolean {
  if (annotation.type === 'highlighter') {
    // 형광펜: 경로 위의 점들과 거리 확인
    for (const p of annotation.points) {
      const screenP = toScreenCoord(p, canvasWidth, canvasHeight);
      const distance = Math.sqrt(
        Math.pow(point.x - screenP.x, 2) + Math.pow(point.y - screenP.y, 2)
      );
      if (distance <= threshold + annotation.strokeWidth / 2) {
        return true;
      }
    }
    return false;
  } else if (annotation.type === 'underline') {
    // 밑줄: 선분까지의 거리 확인
    const start = toScreenCoord(annotation.startPoint, canvasWidth, canvasHeight);
    const end = toScreenCoord(annotation.endPoint, canvasWidth, canvasHeight);

    const distance = pointToLineDistance(point, start, end);
    return distance <= threshold + annotation.strokeWidth / 2;
  }

  return false;
}

/**
 * 점에서 선분까지의 거리 계산
 */
function pointToLineDistance(
  point: { x: number; y: number },
  lineStart: { x: number; y: number },
  lineEnd: { x: number; y: number }
): number {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    // 선분이 점인 경우
    return Math.sqrt(
      Math.pow(point.x - lineStart.x, 2) + Math.pow(point.y - lineStart.y, 2)
    );
  }

  // 선분 위의 가장 가까운 점 파라미터 t (0~1로 클램프)
  let t =
    ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t));

  // 가장 가까운 점
  const closestX = lineStart.x + t * dx;
  const closestY = lineStart.y + t * dy;

  return Math.sqrt(Math.pow(point.x - closestX, 2) + Math.pow(point.y - closestY, 2));
}

/**
 * 고유 ID 생성
 */
export function generateAnnotationId(): string {
  return `anno_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * 포인트 샘플링 (성능 최적화)
 * 너무 가까운 포인트는 제거
 * @param points 포인트 배열
 * @param minDistance 최소 거리
 */
export function samplePoints(
  points: AnnotationPoint[],
  minDistance: number = 0.005
): AnnotationPoint[] {
  if (points.length < 2) return points;

  const result: AnnotationPoint[] = [points[0]];

  for (let i = 1; i < points.length; i++) {
    const lastPoint = result[result.length - 1];
    const currentPoint = points[i];

    const distance = Math.sqrt(
      Math.pow(currentPoint.x - lastPoint.x, 2) +
        Math.pow(currentPoint.y - lastPoint.y, 2)
    );

    if (distance >= minDistance) {
      result.push(currentPoint);
    }
  }

  // 마지막 점은 항상 포함
  const lastOriginal = points[points.length - 1];
  const lastSampled = result[result.length - 1];
  if (lastOriginal.x !== lastSampled.x || lastOriginal.y !== lastSampled.y) {
    result.push(lastOriginal);
  }

  return result;
}

/**
 * Shift 키 눌렀을 때 수평/수직 정렬
 * @param startPoint 시작점
 * @param endPoint 끝점
 * @returns 정렬된 끝점
 */
export function snapToAxis(
  startPoint: AnnotationPoint,
  endPoint: AnnotationPoint
): AnnotationPoint {
  const dx = Math.abs(endPoint.x - startPoint.x);
  const dy = Math.abs(endPoint.y - startPoint.y);

  // 수평이 더 가까우면 y를 시작점에 맞춤
  if (dx > dy) {
    return { x: endPoint.x, y: startPoint.y };
  }
  // 수직이 더 가까우면 x를 시작점에 맞춤
  return { x: startPoint.x, y: endPoint.y };
}
